
import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import NoResultsArticles from "./NoResultsArticles";
import { useIsMobile } from "@/hooks/use-mobile";

interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  image: string;
  slug: string;
  publishedAt: string;
  readTime: string;
}

interface ArticleGridProps {
  filteredArticles: Article[];
  searchTerm: string;
  onClearSearch: () => void;
}

const ArticleGrid: React.FC<ArticleGridProps> = ({ 
  filteredArticles, 
  searchTerm, 
  onClearSearch 
}) => {
  // Create refs array for all articles
  const articleRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  
  // Setup visibility state for all articles
  const [visibleArticles, setVisibleArticles] = useState<boolean[]>([]);
  
  // Check if device is mobile
  const isMobile = useIsMobile();
  
  // Initialize visibility state based on number of articles
  useEffect(() => {
    // On mobile, make all articles immediately visible for better performance
    if (isMobile) {
      setVisibleArticles(new Array(filteredArticles.length).fill(true));
    } else {
      setVisibleArticles(new Array(filteredArticles.length).fill(false));
    }
    
    // Pre-fill the refs array
    articleRefs.current = articleRefs.current.slice(0, filteredArticles.length);
    while (articleRefs.current.length < filteredArticles.length) {
      articleRefs.current.push(null);
    }
  }, [filteredArticles.length, isMobile]);
  
  // Setup intersection observer only for desktop
  useEffect(() => {
    // Skip observer on mobile
    if (isMobile) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = articleRefs.current.findIndex(ref => ref === entry.target);
          if (index !== -1 && entry.isIntersecting) {
            setVisibleArticles(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    
    // Observe all article refs
    articleRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    
    // Cleanup
    return () => {
      articleRefs.current.forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [filteredArticles.length, isMobile]);
  
  return (
    <section className="py-6 md:py-8">
      <div className="container-custom">
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredArticles.map((article, index) => (
              <Link 
                key={article.id} 
                to={`/article/${article.slug}`} 
                className="group hover-card"
                ref={el => articleRefs.current[index] = el}
                style={{
                  opacity: visibleArticles[index] ? 1 : 0,
                  transform: visibleArticles[index] ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.5s ease-out ${index * 0.1}s`,
                }}
              >
                <article className="bg-white rounded-lg overflow-hidden shadow-md transition-all h-full flex flex-col">
                  <div className="aspect-video image-hover">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-6 flex flex-col flex-grow">
                    <div className="mb-2">
                      <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                        {article.category}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-4 flex-grow line-clamp-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                      <div className="text-xs md:text-sm text-muted-foreground">{article.publishedAt}</div>
                      <div className="flex items-center text-xs md:text-sm text-muted-foreground">
                        <Clock className="h-3 w-3 mr-1" />
                        {article.readTime}
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        ) : (
          <NoResultsArticles searchTerm={searchTerm} onClearSearch={onClearSearch} />
        )}
      </div>
    </section>
  );
};

export default ArticleGrid;
